import { useEffect, useCallback, useState } from 'react';
import { driver, DriveStep } from 'driver.js';
import 'driver.js/dist/driver.css';
import { useOnboardingTour } from '@/hooks/useOnboardingTour';

interface OnboardingTourProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
}

export const OnboardingTour = ({ activeTab, onTabChange }: OnboardingTourProps) => {
  const { shouldShowTour, completeTour, isFirstVisit } = useOnboardingTour();
  const [tourStarted, setTourStarted] = useState(false);

  const startTour = useCallback(() => {
    if (tourStarted) return;
    setTourStarted(true);

    // Start on values tab to follow the journey progression
    if (activeTab !== 'values') {
      onTabChange('values');
    }

    const steps: DriveStep[] = [
      {
        popover: {
          title: '👋 Welcome to MomentumEQ!',
          description: 'Your personal career growth companion. Let me give you a quick tour of the key navigation areas.',
          side: 'over',
          align: 'center',
        },
      },
      {
        element: '[data-tour="settings-link"]',
        popover: {
          title: '⚙️ Settings & Profile',
          description: 'Complete your career profile here first! It powers all the AI features.',
          side: 'bottom',
          align: 'end',
        },
      },
      {
        element: '[data-tour="journey-progress"]',
        popover: {
          title: '🗺️ Your Journey Progress',
          description: 'This is your navigation bar showing your growth journey. Click any phase to jump directly to it.',
          side: 'bottom',
          align: 'center',
        },
      },
      {
        element: '[data-tour="tab-values"]',
        popover: {
          title: '❤️ Phase 1: Core Values',
          description: 'Start here! Discover what truly matters to you. Your values form the foundation for all career decisions.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-tour="tab-goals"]',
        popover: {
          title: '🎯 Phase 2: Goals',
          description: 'Set meaningful career goals using the 3E framework: Education, Exposure, and Experience.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-tour="tab-plan"]',
        popover: {
          title: '🗺️ Phase 3: Development Plan',
          description: 'Generate an AI-powered development plan with milestones tailored to your goals.',
          side: 'bottom',
          align: 'center',
        },
      },
      {
        element: '[data-tour="tab-habits"]',
        popover: {
          title: '✅ Phase 4: Habits',
          description: 'Build daily habits that drive consistent progress. Track your streaks!',
          side: 'bottom',
          align: 'end',
        },
      },
    ];

    const driverObj = driver({
      showProgress: true,
      steps,
      animate: true,
      smoothScroll: true,
      allowClose: true,
      overlayColor: 'hsl(var(--background) / 0.75)',
      stagePadding: 8,
      stageRadius: 8,
      popoverClass: 'onboarding-popover',
      nextBtnText: 'Next →',
      prevBtnText: '← Back',
      doneBtnText: 'Finish Tour',
      onDestroyStarted: () => {
        completeTour();
        driverObj.destroy();
      },
    });

    // Small delay to ensure DOM is ready
    setTimeout(() => {
      driverObj.drive();
    }, 500);
  }, [activeTab, onTabChange, completeTour, tourStarted]);

  useEffect(() => {
    if (shouldShowTour && isFirstVisit && !tourStarted) {
      startTour();
    }
  }, [shouldShowTour, isFirstVisit, startTour, tourStarted]);

  return null; // This component doesn't render anything visible
};

export default OnboardingTour;
